import React, {
  createContext,
  useContext,
  useEffect,
  ReactNode,
  useRef,
  useState,
} from "react";
import { EventEmitter } from "events";

// Define connection status enum
export enum ConnectionStatus {
  CONNECTING = "connecting",
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  ERROR = "error"
}

interface WebSocketContextType {
  eventBus: EventEmitter;
  setToken: (token: string | null) => void;
  connectionStatus: ConnectionStatus;
  lastError: string | null;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const eventBusRef = useRef<EventEmitter>(new EventEmitter());
  const eventBus = eventBusRef.current;

  const [token, setToken] = useState<string | null>(() => localStorage.getItem("token"));
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    token ? ConnectionStatus.CONNECTING : ConnectionStatus.DISCONNECTED
  );
  const [lastError, setLastError] = useState<string | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  const lastPingTimeRef = useRef<number>(Date.now());
  const tokenRef = useRef(token);

  // Set the maximum number of listeners to avoid memory leaks
  useEffect(() => {
    eventBus.setMaxListeners(20);
    return () => {
      eventBus.removeAllListeners();
    };
  }, [eventBus]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Handle page unload to properly close the WebSocket connection
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, "Page unloaded");
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Cleanup function for all timers and connections
  const cleanup = () => {
    if (wsRef.current) {
      wsRef.current.close(1000, "Component unmounted");
      wsRef.current = null;
    }
    
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    
    if (heartbeatTimerRef.current) {
      window.clearTimeout(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    
    if (!token) {
      setConnectionStatus(ConnectionStatus.DISCONNECTED);
    }
  };

  // Check the WebSocket connection health. Server sends PING every 20s.
  // Use 90s threshold to avoid false closes (was 30s, caused tr_progress drops).
  const checkConnection = () => {
    const now = Date.now();
    const timeSinceLastPing = now - lastPingTimeRef.current;

    if (timeSinceLastPing > 90000 && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.warn("No ping received in 90s, closing connection for reconnect");
      setConnectionStatus(ConnectionStatus.DISCONNECTED);
      wsRef.current.close();
    }

    heartbeatTimerRef.current = window.setTimeout(checkConnection, 15000);
  };

  useEffect(() => {
    return cleanup;
  }, []);

  const connectWebSocket = () => {
    if (!tokenRef.current) return;

    setConnectionStatus(ConnectionStatus.CONNECTING);
    setLastError(null);

    const rawBaseUrl = process.env.REACT_APP_BASE_URL;
    if (!rawBaseUrl) {
      const error = "REACT_APP_BASE_URL is missing or invalid";
      console.error(error);
      setLastError(error);
      setConnectionStatus(ConnectionStatus.ERROR);
      return;
    }

    let fullUrl: string;
    try {
      const baseUrl = new URL(rawBaseUrl);
      // FastAPI mounts /ws on the app root. REACT_APP_BASE_URL often includes /v2 for REST;
      // resolving "ws" against that base would yield /v2/ws (wrong). Always use API origin.
      const wsOrigin = new URL(baseUrl.origin);
      wsOrigin.protocol = wsOrigin.protocol === "https:" ? "wss:" : "ws:";
      const wsEndpoint = new URL("/ws", `${wsOrigin.href.replace(/\/+$/, "")}/`);
      wsEndpoint.searchParams.set("token", tokenRef.current);
      fullUrl = wsEndpoint.toString();
    } catch {
      const error = "REACT_APP_BASE_URL is missing or invalid";
      console.error(error);
      setLastError(error);
      setConnectionStatus(ConnectionStatus.ERROR);
      return;
    }

    const newWs = new WebSocket(fullUrl);
    wsRef.current = newWs;

    newWs.onopen = () => {
      setConnectionStatus(ConnectionStatus.CONNECTED);
      setLastError(null);
      // Reset ping timer so the new connection gets a full 30s to receive first PING
      lastPingTimeRef.current = Date.now();

      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // Start the heartbeat check when connection opens
      if (heartbeatTimerRef.current) {
        window.clearTimeout(heartbeatTimerRef.current);
      }
      heartbeatTimerRef.current = window.setTimeout(checkConnection, 10000);
    };

    newWs.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        const { type, data, status } = message;
        
        // Update last ping time whenever we receive a PING message
        if (type === "PING") {
          lastPingTimeRef.current = Date.now();
          return;
        }

        if (status >= 400) {
          // If the status is 401, remove the token and close the WebSocket connection
          const errorMsg = "Unauthorized (401). Removing token and disconnecting WebSocket.";
          console.warn(errorMsg);
          setLastError(errorMsg);
          setConnectionStatus(ConnectionStatus.ERROR);
          handleSetToken(null);
          newWs.close();
          return;
        }

        // Normalize legacy PROGRESS to typed events by job_type
        let emitType = type;
        if (type === "PROGRESS" && data && typeof data === "object" && "job_type" in data) {
          const jobType = (data as { job_type?: string }).job_type;
          if (jobType === "translation") emitType = "tr_progress";
          else if (jobType === "voiceover") emitType = "vo_progress";
          else if (jobType === "transcript") emitType = "ts_progress";
        }
        eventBus.emit(emitType, data);
      } catch (error) {
        const errorMsg = `Error parsing WebSocket message: ${error}`;
        console.error(errorMsg);
        setLastError(errorMsg);
      }
    };

    newWs.onclose = (event) => {
      // Only clear ref if this is the active connection (avoids Strict Mode race:
      // old ws closes async and would overwrite ref of newly created ws)
      if (wsRef.current === newWs) {
        wsRef.current = null;
      }
      if (event.code !== 1000) {
        console.warn(`WebSocket closed: code=${event.code} reason=${event.reason || "none"} (1006=connection failed, 1008=policy)`);
      }
      if (connectionStatus === ConnectionStatus.CONNECTED) {
        setConnectionStatus(ConnectionStatus.DISCONNECTED);
      }
      if (heartbeatTimerRef.current) {
        window.clearTimeout(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (tokenRef.current && (event.code !== 1000 || event.wasClean === false)) {
        // Use exponential backoff for reconnection
        const backoffTime = calculateBackoff(event);
        const message = `WebSocket closed. Reconnecting in ${backoffTime}ms`;
        console.log(message);
        
        reconnectTimerRef.current = window.setTimeout(() => {
          connectWebSocket();
        }, backoffTime);
      }
    };

    newWs.onerror = () => {
      const url = fullUrl.split("?")[0];
      const errorMsg = `WebSocket connection failed (${url}). Check: 1) backend running, 2) correct REACT_APP_BASE_URL, 3) no mixed content (https page needs wss://).`;
      console.error(errorMsg);
      setLastError(errorMsg);
      setConnectionStatus(ConnectionStatus.ERROR);
    };
  };
  
  // Calculate reconnection backoff time based on the close event
  const calculateBackoff = (closeEvent: CloseEvent): number => {
    // Default to 2 seconds
    let backoff = 2000;
    
    // Increase backoff for certain error codes
    if (closeEvent.code === 1008 || closeEvent.code === 1011) {
      // Policy violation or internal error - wait longer
      backoff = 5000;
    } else if (closeEvent.code === 1006) {
      // Abnormal closure - could be network issues
      backoff = 3000;
    }
    
    // Add some randomness to prevent all clients reconnecting simultaneously
    return backoff + Math.floor(Math.random() * 1000);
  };

  useEffect(() => {
    if (!token) {
      cleanup();
      return;
    }

    if (!wsRef.current) {
      connectWebSocket();
    }

    return () => {
      cleanup();
    };
  }, [token]);

  const handleSetToken = (newToken: string | null) => {
    if (newToken) {
      localStorage.setItem("token", newToken);
    } else {
      localStorage.removeItem("token");
    }
    tokenRef.current = newToken;
    setToken(newToken);
  };

  return (
    <WebSocketContext.Provider
      value={{
        eventBus,
        setToken: handleSetToken,
        connectionStatus,
        lastError,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
